import { useState, useEffect } from "react";
import {
    DEFAULT_AVAILABILITY_TIME_SLOTS,
    FULL_DAY_TIME_SLOTS,
    normalizeAvailabilityForWizard,
    useStudioErstellen
} from "../../store/studioErstellenStore";
import Modal from "../UI/Modal";

import classes from "./Availability.module.css";

const DEFAULT_TIME_SLOTS = DEFAULT_AVAILABILITY_TIME_SLOTS;

const DayComponent = ({ dayName, dayData, onDayDataChange, dayKey, allDaysData, onCopyToOtherDays }) => {
    const isDefaultTime = (slots) => {
        if (!slots || slots.length !== 1) return false;
        return slots[0].start === '07:00' && slots[0].end === '21:00';
    };

    const isFullDayTime = (slots) => {
        if (!slots || slots.length !== 1) return false;
        return slots[0].start === '00:00' && slots[0].end === '23:59';
    };

    const getInitialTimeSlots = () => {
        if (dayData.timeSlots && dayData.timeSlots.length > 0 && dayData.timeSlots[0].start && dayData.timeSlots[0].end) {
            return dayData.timeSlots;
        }
        return DEFAULT_TIME_SLOTS;
    };

    const initialTimeSlots = getInitialTimeSlots();
    const [isActive, setIsActive] = useState(dayData.isActive ?? true);
    const [is24Hours, setIs24Hours] = useState(dayData.is24Hours ?? false);
    const [timeSlots, setTimeSlots] = useState(initialTimeSlots);
    const [hasUserChangedTimes, setHasUserChangedTimes] = useState(!isDefaultTime(initialTimeSlots));
    const [showCopyIcon, setShowCopyIcon] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDays, setSelectedDays] = useState([]);

    // Initialize parent with default times if not already set
    useEffect(() => {
        if (!dayData.timeSlots || dayData.timeSlots.length === 0 || !dayData.timeSlots[0].start || !dayData.timeSlots[0].end) {
            updateParent(isActive, is24Hours, DEFAULT_TIME_SLOTS);
        }
    }, []);

    // Sync internal state when dayData prop changes (e.g., after copying from another day)
    useEffect(() => {
        setIsActive(dayData.isActive ?? true);
        setIs24Hours(dayData.is24Hours ?? false);
        const newTimeSlots = getInitialTimeSlots();
        setTimeSlots(newTimeSlots);
        // If times are different from default, mark as user-changed
        if (!isDefaultTime(newTimeSlots)) {
            setHasUserChangedTimes(true);
        } else {
            setHasUserChangedTimes(false);
        }
    }, [dayData]);

    useEffect(() => {
        if (isActive && !is24Hours) {
            // Only show copy icon if user has actually changed times from default
            setShowCopyIcon(hasUserChangedTimes && !isDefaultTime(timeSlots));
        } else {
            setShowCopyIcon(false);
        }
    }, [timeSlots, isActive, is24Hours, hasUserChangedTimes]);

    const updateParent = (newIsActive, newIs24Hours, newTimeSlots) => {
        onDayDataChange({
            isActive: newIsActive,
            is24Hours: newIs24Hours,
            timeSlots: newTimeSlots
        });
    };

    const handleActiveChange = (newIsActive) => {
        setIsActive(newIsActive);
        updateParent(newIsActive, is24Hours, timeSlots);
    };

    const handle24HoursChange = (newIs24Hours) => {
        const nextTimeSlots = newIs24Hours
            ? FULL_DAY_TIME_SLOTS
            : isFullDayTime(timeSlots)
                ? DEFAULT_TIME_SLOTS
                : timeSlots;
        setIs24Hours(newIs24Hours);
        setTimeSlots(nextTimeSlots);
        updateParent(isActive, newIs24Hours, nextTimeSlots);
    };

    const generateHours = () => {
        const hours = [];
        for (let i = 0; i <= 24; i++) {
            hours.push(i.toString().padStart(2, '0') + ':00');
        }
        return hours;
    };

    // Get available start times based on existing slots
    const getAvailableStartTimes = (slotIndex) => {
        const hours = generateHours();
        if (slotIndex === 0) return hours;

        const previousSlot = timeSlots[slotIndex - 1];
        if (!previousSlot.end) return hours;

        const previousEndHour = parseInt(previousSlot.end.split(':')[0]);
        return hours.filter(time => parseInt(time.split(':')[0]) >= previousEndHour);
    };

    const getAvailableEndTimes = (startTime, slotIndex) => {
        if (!startTime) return [];
        const hours = generateHours();
        const startHour = parseInt(startTime.split(':')[0]);
        return hours.filter(time => parseInt(time.split(':')[0]) > startHour);
    };
    
    const canAddNewSlot = () => {
        const lastSlot = timeSlots[timeSlots.length - 1];
        return lastSlot.end && parseInt(lastSlot.end.split(':')[0]) < 24;
    };

    const handleTimeChange = (index, field, value) => {
        const newTimeSlots = [...timeSlots];
        newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };
        if (field === 'start') {
            const availableEndTimes = getAvailableEndTimes(value, index);
            newTimeSlots[index].end = availableEndTimes[0] || '';
        }
        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    const formatTimeSlots = (slots) => {
        if (!slots || slots.length === 0) return '';
        const formatted = slots
            .filter(slot => slot.start && slot.end)
            .map(slot => `${slot.start} - ${slot.end} Uhr`);
        return formatted.join('\n');
    };

    const compareTimeSlots = (slots1, slots2) => {
        if (!slots1 || !slots2 || slots1.length !== slots2.length) return false;
        
        const normalizeSlots = (slots) => {
            return slots
                .filter(slot => slot.start && slot.end)
                .map(slot => `${slot.start}-${slot.end}`)
                .sort()
                .join(',');
        };
        
        return normalizeSlots(slots1) === normalizeSlots(slots2);
    };

    const hasSameTimeSlots = (day) => {
        if (day === dayKey) return true;
        const targetDayData = allDaysData[day];
        if (!targetDayData) return false;
        
        // If target day has 24 hours enabled, it's not the same (allow copying to it)
        if (targetDayData.is24Hours) return false;
        
        return compareTimeSlots(timeSlots, targetDayData.timeSlots) &&
               isActive === targetDayData.isActive &&
               is24Hours === targetDayData.is24Hours;
    };

    const handleCopyClick = () => {
        setShowModal(true);
        setSelectedDays([]);
    };

    const handleDayToggle = (day) => {
        // Don't allow toggling if day has same time slots or is the source day
        if (day === dayKey || hasSameTimeSlots(day)) return;
        
        setSelectedDays(prev => 
            prev.includes(day) 
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    };

    const handleCopyTimes = () => {
        if (selectedDays.length === 0) return;
        // When copying time slots, always set is24Hours to false
        onCopyToOtherDays(dayKey, selectedDays, {
            isActive: isActive,
            is24Hours: false, // Always disable 24 hours when copying time slots
            timeSlots: timeSlots
        });
        setShowModal(false);
        setSelectedDays([]);
    };

    const dayNamesMap = {
        monday: 'Montag',
        tuesday: 'Dienstag',
        wednesday: 'Mittwoch',
        thursday: 'Donnerstag',
        friday: 'Freitag',
        saturday: 'Samstag',
        sunday: 'Sonntag'
    };

    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    const addNewTimeSlot = () => {
        const newTimeSlots = [...timeSlots, { start: '', end: '' }];
        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    const deleteTimeSlot = (index) => {
        const newTimeSlots = timeSlots.filter((_, i) => i !== index);
        setTimeSlots(newTimeSlots);
        setHasUserChangedTimes(true); // Mark that user has changed times
        updateParent(isActive, is24Hours, newTimeSlots);
    };

    return (
        <div className={classes.dayContainer}>
        <div className={classes.dayContainerInner}>
            <div className={classes.day}>
                <div className={classes.switchContainer}>
                    <label className={classes.switch}>
                        <input 
                            type="checkbox" 
                            checked={isActive}
                            onChange={(e) => handleActiveChange(e.target.checked)}
                        />
                        <span className={classes.slider}></span>
                    </label>
                </div>
                <span className="titleSemibold3">{dayName}</span>
            </div>
            {isActive && (
                <div className={classes.periodContainer}>
                    <div 
                        className={`${classes.period} ${!is24Hours ? classes.selected : ''}`}
                        onClick={() => handle24HoursChange(false)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                            <path d="M14 7.56774C14 11.11 11.0951 13.9909 7.5 13.9909C3.90493 13.9909 1 11.11 1 7.56774C1 4.02548 3.90493 1.14453 7.5 1.14453C11.0951 1.14453 14 4.02548 14 7.56774Z" stroke="#1F1F1F"/>
                            <ellipse cx="7.5" cy="7.56782" rx="4.5" ry="4.45063" fill="#1F1F1F"/>
                        </svg>
                        <span className="semiboldMiddle">Stunden auswählen</span>
                    </div>
                    <div 
                        className={`${classes.period} ${is24Hours ? classes.selected : ''}`}
                        onClick={() => handle24HoursChange(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                            <path d="M14 7.56774C14 11.11 11.0951 13.9909 7.5 13.9909C3.90493 13.9909 1 11.11 1 7.56774C1 4.02548 3.90493 1.14453 7.5 1.14453C11.0951 1.14453 14 4.02548 14 7.56774Z" stroke="#1F1F1F"/>
                            <ellipse cx="7.5" cy="7.56782" rx="4.5" ry="4.45063" fill="#1F1F1F"/>
                        </svg>
                        <span className="semiboldMiddle">24 Stunden</span>
                    </div>
                </div>
            )}
        </div>
        {isActive && !is24Hours && (
        <div className={classes.timesAndCopyContainer}>
        <div className={classes.timesContainer}>
        {timeSlots.map((slot, index) => (
        <div key={index} className={classes.timeInputContainer}>
            <div className={classes.timeSelect}>
                <div className={classes.selectWrapper}>
                    <select
                        value={slot.start}
                        onChange={(e) => handleTimeChange(index, 'start', e.target.value)}
                        className="semiboldMiddle"
                    >
                        <option value="">Start</option>
                        {getAvailableStartTimes(index).map((time) => (
                            <option key={time} value={time}>
                                {time}
                            </option>
                        ))}
                    </select>
                    <div className={classes.selectContent}>
                        <span className={`semiboldMiddle ${isDefaultTime(timeSlots) && slot.start === '07:00' ? classes.defaultTime : ''}`}>{slot.start || 'Start'}</span>
                        <svg className={classes.selectArrow} xmlns="http://www.w3.org/2000/svg" width="14" height="7" viewBox="0 0 14 7" fill="none">
                            <path d="M6.87521 7L6.50678 6.63158L0.888364 1.01316L1.90152 1.35096e-07L6.87521 4.97368L12.2173 1.20818e-08L13.2305 1.10526L6.87521 7Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                </div>
                <span className={classes.dash}>-</span>
                <span className={`semiboldMiddle ${classes.smallerSpan}`}>Ende</span>
                <div className={classes.selectWrapper}>
                    <select
                        value={slot.end}
                        onChange={(e) => handleTimeChange(index, 'end', e.target.value)}
                        disabled={!slot.start} 
                        className="semiboldMiddle"
                    >
                        <option value="">Ende</option>
                            {getAvailableEndTimes(slot.start, index).map((time) => (
                                <option key={time} value={time}>
                                    {time}
                                </option>
                            ))}
                    </select>
                    <div className={classes.selectContent}>
                        <span className={`semiboldMiddle ${isDefaultTime(timeSlots) && slot.end === '21:00' ? classes.defaultTime : ''}`}>{slot.end || 'Ende'}</span>
                        <svg className={classes.selectArrow} xmlns="http://www.w3.org/2000/svg" width="14" height="7" viewBox="0 0 14 7" fill="none">
                            <path d="M6.87521 7L6.50678 6.63158L0.888364 1.01316L1.90152 1.35096e-07L6.87521 4.97368L12.2173 1.20818e-08L13.2305 1.10526L6.87521 7Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                </div>
            </div>
            {index !== 0 && (
                <button 
                    className={classes.deleteButton}
                    onClick={() => deleteTimeSlot(index)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.8008 6.33789L0.5 1.03709L1.1992 0.337891L6.5 5.63869L11.8008 0.337891L12.5 1.03709L7.1992 6.33789L12.5 11.6387L11.8008 12.3379L6.5 7.03709L1.1992 12.3379L0.5 11.6387L5.8008 6.33789Z" fill="#1F1F1F"/>
                    </svg>
                </button>
                )}
            </div>
            ))}
            <button 
                className={`${classes.addSlot} semiboldXs`}
                onClick={addNewTimeSlot}
                disabled={!canAddNewSlot()}
                style={{ cursor: !canAddNewSlot() ? 'context-menu' : 'pointer' }}
            >
                Zeitslot hinzufügen
            </button>
        </div>
        {showCopyIcon && (
            <div onClick={handleCopyClick} className={classes.copyContainer}>
                <div 
                    className={classes.copyIconWrapper}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {showTooltip && (
                        <div className={classes.tooltip}>
                            Zeiten für andere Tage übernehmen
                            <div className={classes.tooltipArrow}></div>
                        </div>
                    )}
                
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="24" viewBox="0 0 20 24" fill="none">
                        <path d="M13 21.8555V23.3555H3V21.8555H13ZM14.5 20.3555V6.35547C14.5 5.52704 13.8284 4.85547 13 4.85547H3C2.17157 4.85547 1.5 5.52704 1.5 6.35547V20.3555C1.5 21.1839 2.17157 21.8555 3 21.8555V23.3555L2.8457 23.3516C1.31166 23.2738 0.0816253 22.0438 0.00390625 20.5098L0 20.3555V6.35547C0 4.69861 1.34315 3.35547 3 3.35547H13C14.6569 3.35547 16 4.69861 16 6.35547V20.3555C16 22.0123 14.6569 23.3555 13 23.3555V21.8555C13.8284 21.8555 14.5 21.1839 14.5 20.3555Z" fill="#1F1F1F"/>
                        <path d="M17.8213 3.75C17.8213 2.50736 16.8139 1.5 15.5713 1.5H3.42773V0H15.5713C17.6424 0 19.3213 1.67893 19.3213 3.75V19.9609H17.8213V3.75Z" fill="#1F1F1F"/>
                    </svg>
                    
                </div>
            </div>
        )}
        </div>
        )}
        {showModal && (
            <Modal open={showModal} onBackdropClick={() => setShowModal(false)} className="availabilityTimeModal">
                <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
                    <header className={classes.modalHeader}>
                        <h2 className="mediumLarge">Zeiten für andere Tage übernehmen</h2>
                        <button 
                            className={classes.modalCloseButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowModal(false);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                    </header>
                    <div className={classes.modalBody}>
                        <div className={classes.modalSection}>
                            <h3 className="semiboldMiddle">Betriebszeit</h3>
                            <div className={classes.timeSlotsDisplay}>
                                {timeSlots
                                    .filter(slot => slot.start && slot.end)
                                    .map((slot, index) => (
                                        <p key={index} className="mediumLarge">
                                            {slot.start} - {slot.end} Uhr
                                        </p>
                                    ))}
                            </div>
                        </div>
                        <div className={classes.modalDaysList}>
                            {allDays.map(day => {
                                const isSourceDay = day === dayKey;
                                const hasSameSlots = hasSameTimeSlots(day);
                                const isDisabled = isSourceDay || hasSameSlots;
                                
                                return (
                                    <div 
                                        onClick={() => !isDisabled && handleDayToggle(day)} 
                                        key={day} 
                                        className={`${classes.modalDayItem} ${isDisabled ? classes.modalDayItemDisabled : ''}`}
                                    >
                                        <span className="semiboldMiddle">{dayNamesMap[day]}</span>

                                        {isDisabled ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <rect x="0.3" y="0.3" width="23.4" height="23.4" rx="5.7" fill="#B1B6BB" stroke="#B1B6BB" strokeWidth="0.6"/>
                                                <line x1="6.55579" y1="11.8172" x2="10.1295" y2="15.4678" stroke="white" strokeWidth="1.55556"/>
                                                <line x1="9.03867" y1="15.4501" x2="17.464" y2="7.4363" stroke="white" strokeWidth="1.55556"/>
                                            </svg>
                                        ) : (
                                            <input
                                                type="checkbox"
                                                checked={selectedDays.includes(day)}
                                                readOnly
                                                className={classes.dayCheckbox}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        
                    </div>
                    
                    <div className={classes.modalFooter}>
                        <button
                            className={`${classes.copyButton} mediumLarge`}
                            onClick={handleCopyTimes}
                            disabled={selectedDays.length === 0}
                        >
                            Zeiten übernehmen
                        </button>
                    </div>

                </div>
            </Modal>
        )}
    </div>
    );
};

export const Availability = ({ validate, fromEditor }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    const containerClassName = `${classes.container} ${fromEditor ? classes.editorMode : ''}`;

    const normalizedAvailability = normalizeAvailabilityForWizard(studioData?.availability);

    const [daysData, setDaysData] = useState({
        monday: { 
            isActive: normalizedAvailability.Mon.isActive,
            is24Hours: normalizedAvailability.Mon.is24Hours,
            timeSlots: normalizedAvailability.Mon.timeSlots
        },
        tuesday: { 
            isActive: normalizedAvailability.Di.isActive,
            is24Hours: normalizedAvailability.Di.is24Hours,
            timeSlots: normalizedAvailability.Di.timeSlots
        },
        wednesday: { 
            isActive: normalizedAvailability.Mi.isActive,
            is24Hours: normalizedAvailability.Mi.is24Hours,
            timeSlots: normalizedAvailability.Mi.timeSlots
        },
        thursday: { 
            isActive: normalizedAvailability.Do.isActive,
            is24Hours: normalizedAvailability.Do.is24Hours,
            timeSlots: normalizedAvailability.Do.timeSlots
        },
        friday: { 
            isActive: normalizedAvailability.Fr.isActive,
            is24Hours: normalizedAvailability.Fr.is24Hours,
            timeSlots: normalizedAvailability.Fr.timeSlots
        },
        saturday: { 
            isActive: normalizedAvailability.Sa.isActive,
            is24Hours: normalizedAvailability.Sa.is24Hours,
            timeSlots: normalizedAvailability.Sa.timeSlots
        },
        sunday: { 
            isActive: normalizedAvailability.So.isActive,
            is24Hours: normalizedAvailability.So.is24Hours,
            timeSlots: normalizedAvailability.So.timeSlots
        },
    });

    // Initialize availability with default times if not set
    useEffect(() => {
        updateStudioData('availability', normalizedAvailability);
    }, []);

    const handleDayDataChange = (day, newData) => {
        setDaysData(prev => ({
            ...prev,
            [day]: newData
        }));
    
        const dayMap = {
            monday: 'Mon',
            tuesday: 'Di',
            wednesday: 'Mi',
            thursday: 'Do',
            friday: 'Fr',
            saturday: 'Sa',
            sunday: 'So'
        };
    
        updateStudioData('availability', {
            ...studioData.availability,
            [dayMap[day]]: newData
        });
    };

    const handleCopyToOtherDays = (sourceDay, targetDays, sourceData) => {
        const dayMap = {
            monday: 'Mon',
            tuesday: 'Di',
            wednesday: 'Mi',
            thursday: 'Do',
            friday: 'Fr',
            saturday: 'Sa',
            sunday: 'So'
        };

        const newDaysData = { ...daysData };
        const newAvailability = { ...studioData.availability };

        targetDays.forEach(targetDay => {
            newDaysData[targetDay] = { ...sourceData };
            newAvailability[dayMap[targetDay]] = { ...sourceData };
        });

        setDaysData(newDaysData);
        updateStudioData('availability', newAvailability);
    };

  return (
    <div className={containerClassName}>
        <header className={classes.header}>
            <h1 className="titleSemibold">Betriebszeiten</h1>
            <p style={{display: fromEditor ? 'none' : 'block'}} className="slimRegularLargeText">
            Lege deine Betriebszeiten fest, um Gästen zu zeigen, wann dein Raum buchbar ist. Reservierungen sind nur während dieser Zeiten möglich.
            </p>
        </header>
        <div className={classes.content}>
            <DayComponent 
                dayName="Mo" 
                dayData={daysData.monday}
                onDayDataChange={(newData) => handleDayDataChange('monday', newData)}
                dayKey="monday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="Di" 
                dayData={daysData.tuesday}
                onDayDataChange={(newData) => handleDayDataChange('tuesday', newData)}
                dayKey="tuesday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="Mi" 
                dayData={daysData.wednesday}
                onDayDataChange={(newData) => handleDayDataChange('wednesday', newData)}
                dayKey="wednesday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="Do" 
                dayData={daysData.thursday}
                onDayDataChange={(newData) => handleDayDataChange('thursday', newData)}
                dayKey="thursday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="Fr" 
                dayData={daysData.friday}
                onDayDataChange={(newData) => handleDayDataChange('friday', newData)}
                dayKey="friday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="Sa" 
                dayData={daysData.saturday}
                onDayDataChange={(newData) => handleDayDataChange('saturday', newData)}
                dayKey="saturday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
            <div className={classes.horrizontalLine}></div>
            <DayComponent 
                dayName="So" 
                dayData={daysData.sunday}
                onDayDataChange={(newData) => handleDayDataChange('sunday', newData)}
                dayKey="sunday"
                allDaysData={daysData}
                onCopyToOtherDays={handleCopyToOtherDays}
            />
        </div>
    </div>
  );
};

export default Availability;
